import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import FilterPopover from '../../community/residents/components/FilterPopover.jsx';
import UserGroupEmptyState from './UserGroupEmptyState.jsx';

/**
 * Column definitions for the user groups table.
 */
export const GROUP_COLUMN_DEFS = [
  { key: 'group_name', label: 'Group Name', sortable: true, filterable: true, required: true },
  { key: 'capcode', label: 'Pager Channel', sortable: true, filterable: true, required: false },
  { key: 'user_count', label: 'Users', sortable: true, filterable: false, required: true },
];

/**
 * UserGroupsTable — data table for user groups with sorting, filtering,
 * sticky header, row selection, and row actions.
 */
const UserGroupsTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onEditGroup,
  onDeleteGroup,
  onAddGroup,
  onClearFilters,
}) => {
  const activeColumns = useMemo(
    () => GROUP_COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = useCallback(
    (columnKey) => {
      const values = new Set();
      (allData || data).forEach((row) => {
        const val = row[columnKey];
        values.add(val != null && val !== '' ? String(val) : '(empty)');
      });
      return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    },
    [allData, data]
  );

  const allSelected = data.length > 0 && data.every((r) => selectedIds.includes(r.group_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg className="up-icon-sm up-sort-icon up-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="up-icon-sm up-sort-icon up-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg className="up-icon-sm up-sort-icon up-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'group_name':
        return (
          <span className="up-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.group_name}
          </span>
        );

      case 'capcode':
        return row.capcode ? (
          <span className="up-cell-text">{row.capcode}</span>
        ) : (
          <span className="up-cell-text--muted">—</span>
        );

      case 'user_count': {
        const users = row.users || [];
        if (users.length === 0) return <span className="up-cell-text--muted">None</span>;
        return <UsersPopoverCell users={users} />;
      }

      default:
        return <span className="up-cell-text">{row[columnKey] || '—'}</span>;
    }
  };

  if (data.length === 0) {
    const hasAnyData = (allData || []).length > 0;
    return (
      <UserGroupEmptyState
        type={hasAnyData ? 'no-results' : 'no-data'}
        onAddGroup={hasAnyData ? undefined : onAddGroup}
        onClearFilters={hasAnyData ? onClearFilters : undefined}
      />
    );
  }

  return (
    <div className="up-table-wrap">
      <table className="up-table">
        <thead className="up-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="up-th up-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={onSelectAll}
                className="up-checkbox"
                aria-label="Select all groups"
              />
            </th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="up-th">
                <div className="up-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="up-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                    />
                  ) : null}
                </div>
              </th>
            ))}

            {/* Actions column */}
            <th scope="col" className="up-th up-th--actions">
              <span className="up-sr-only">Actions</span>
            </th>
          </tr>
        </thead>

        <tbody className="up-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.group_id);
            return (
              <tr
                key={row.group_id}
                className={`up-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                <td className="up-td up-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.group_id)}
                    className="up-checkbox"
                    aria-label={`Select ${row.group_name}`}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="up-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}

                {/* Row actions kebab */}
                <td className="up-td up-td--actions">
                  <GroupRowActions
                    group={row}
                    onEdit={onEditGroup}
                    onDelete={onDeleteGroup}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * UsersPopoverCell — inline popover showing user names and roles in a group.
 */
const UsersPopoverCell = ({ users }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="up-users-cell" ref={ref}>
      <button
        type="button"
        className="up-users-badge"
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        aria-expanded={open}
        aria-label={`${users.length} user${users.length !== 1 ? 's' : ''} in group`}
      >
        <i className="fe fe-users" style={{ fontSize: '12px' }} aria-hidden="true" />
        {users.length}
      </button>
      {open ? (
        <div className="up-users-popover">
          <div className="up-users-popover__title">Users ({users.length})</div>
          <ul className="up-users-popover__list">
            {users.map((u, i) => (
              <li key={u.user_id || i} className="up-users-popover__item">
                <span className="up-users-popover__name">{u.display_name}</span>
                {u.user_role_name ? (
                  <span className="up-users-popover__role">{u.user_role_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

/**
 * GroupRowActions — kebab menu for per-row actions.
 */
const GroupRowActions = ({ group, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="up-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="up-row-actions__trigger"
        aria-label={`Actions for ${group.group_name}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open ? (
        <div className="up-row-actions__menu">
          <button
            type="button"
            onClick={() => { onEdit(group); setOpen(false); }}
            className="up-row-actions__item"
          >
            <svg className="up-icon-md up-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            Edit Group
          </button>
          <button
            type="button"
            onClick={() => { onDelete(group); setOpen(false); }}
            className="up-row-actions__item up-row-actions__item--danger"
          >
            <svg className="up-icon-md up-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
            Delete Group
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default UserGroupsTable;
